import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography } from '@mui/material';
import { subDays, subWeeks, subMonths } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { FBoxContainer, FHButton, FHSkeletonLoader } from '../../components/base';
import {
  btSupervisionActions,
  clientActions,
  clientAssessmentActions,
  clientSupervisionActions,
  groupParentSupervisionActions,
  parentSupervisionActions,
  sessionActions,
  treatmentPlanActions,
} from '../../store';
import { useNonInitialEffect, usePreClinicalGuardianGating } from '../../hooks';
import { FORTA_BLUE } from '../../utils/colors';
import { history, areUserRolesIn } from '../../helpers';
import PageContainer from '../../components/layout/PageContainer';
import ClientInsights from '../../components/layout/dashboard/ClientInsights';
import ActionRequired from '../../components/layout/dashboard/ActionRequired';
import RecentSessions from '../../components/layout/dashboard/RecentSessions';
import QAReviewedSessionNotes from '../../components/layout/dashboard/QAReviewedSessionNotes';
import InProgressTreatmentPlans from '../../components/layout/dashboard/InProgressTreatmentPlans';
import RecentSupervisions from '../../components/layout/dashboard/RecentSupervisions';
import { TreatmentPlanStatus } from '../../components/layout/treatment-plan/constants/general';
import { Roles, SupervisionNoteStatus } from '../../constants';
import { getSessionCardData } from '../utils';
import SessionUtilizationSummary from '../../components/layout/dashboard/utilization/SessionUtilizationSummary';
import InProgressCQAReviews from '../../components/layout/dashboard/InProgressCQAReviews';
import RelationshipManager from './components/relationshipManager';
import YourActivities from './components/yourActivities';

import type { RootState } from '../../store';

const boxContainerStyle = {
  borderRadius: '20px',
  mt: 3,
  flexGrow: 0,
  boxShadow: 'none',
};

const boxContainerHeaderStyle = {
  height: 40,
  flexGrow: 0,
  boxShadow: 'none',
  borderRadius: '16px 16px 0 0',

  '& h4': {
    fontSize: 20,
    fontWeight: 500,
  },
};

const contentStyle = {
  p: '20px',
  height: 'auto',
};

function GeneralDashboard() {
  const { t } = useTranslation('dashboard');
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const abortController = new AbortController();
  const signal = abortController.signal;

  const { user, loading: authLoading } = useSelector((state: any) => state.auth);

  const { list: clientList = [], selected: client } = useSelector(
    (state: RootState) => state.clients,
  );

  const {
    list: sessions = [],
    loading: sessionLoading,
    awaitingGuardianSignatureSession = [],
  } = useSelector((state: RootState) => state.sessions);

  const { list: btSupervisionList = [], loading: btSupervisionLoading } = useSelector(
    (state: RootState) => state.btSupervision,
  );
  const { list: parentSupervisionList = [], loading: parentSupervisionLoading } = useSelector(
    (state: RootState) => state.parentSupervision,
  );
  const { list: clientSupervisionList = [], loading: clientSupervisionsLoading } = useSelector(
    (state: RootState) => state.clientSupervisions,
  );
  const { list: groupParentSupervisionList = [], loading: groupSupervisionsLoading } = useSelector(
    (state: RootState) => state.groupParentSupervision,
  );
  const { list: clientAssessmentList = [], loading: clientAssessmentLoading } = useSelector(
    (state: RootState) => state.clientAssessment,
  );

  const {
    list: treatmentPlans,
    loading: treatmentPlansLoading,
    tpStatusIdByLabel,
  } = useSelector((state: RootState) => state.treatmentPlans);

  const showActionRequiredVBTSection = areUserRolesIn(user?.roles, [Roles.Vbt]);

  const { hideClinicalFeatures, showPreClinicalFeatures } = usePreClinicalGuardianGating();

  // Notice the ! sign. Hence show for all roles except the ones appear below
  const showRecentSessionsSection = !areUserRolesIn(user?.roles, [
    Roles.Bcba,
    Roles.AssistantAssessor,
    Roles.Guardian,
    Roles.Vbt,
    Roles.QaSpecialist,
  ]);

  const showCQAReviewedSessionsSection = areUserRolesIn(user?.roles, [
    Roles.Prbt,
    Roles.Admin,
    Roles.ClientSuccessManager,
    Roles.Biller,
  ]);

  const editingTps = useMemo(() => {
    if (
      !areUserRolesIn(user?.roles, [
        Roles.Admin,
        Roles.Bcba,
        Roles.AssistantAssessor,
        Roles.ClientSuccessManager,
        Roles.Biller,
      ]) ||
      !treatmentPlans ||
      !tpStatusIdByLabel
    )
      return [];

    return treatmentPlans;
  }, [user?.roles, treatmentPlans, tpStatusIdByLabel]);

  useNonInitialEffect(() => {
    // Cleanup function to cancel the request when the component unmounts
    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    if (!user?.roles || user?.roles.length === 0) return;

    if (areUserRolesIn(user?.roles, [Roles.Guardian, Roles.Vbt])) {
      dispatch(sessionActions.getAwaitingGuardianSignatureSession({ getAccessTokenSilently }));
    }

    // Get last 1 month's most recent sessions for BT
    // Get last 1 day's most recent session for overriding roles
    const sessionNoteStartDate = areUserRolesIn(user?.roles, [
      Roles.Admin,
      Roles.Biller,
      Roles.ClientSuccessManager,
      Roles.QaSpecialist,
    ])
      ? subDays(new Date(), 1)
      : subMonths(new Date(), 1);

    // Get last 2 weeks's most recent supervisions for BCBA
    // Get last 1 day's most recent supervisions for overriding roles
    const supervisionNoteStartDate = areUserRolesIn(user?.roles, [
      Roles.Admin,
      Roles.Biller,
      Roles.ClientSuccessManager,
      Roles.QaSpecialist,
    ])
      ? subDays(new Date(), 1)
      : subWeeks(new Date(), 2);
    const endDate = new Date();

    if (
      areUserRolesIn(user?.roles, [
        Roles.Admin,
        Roles.Prbt,
        Roles.ClientSuccessManager,
        Roles.Biller,
        Roles.QaSpecialist,
        Roles.Vbt,
        Roles.LimitedAccessGuardian,
      ])
    ) {
      const params: any = {
        startDate: sessionNoteStartDate,
        endDate,
        include_qa_stats: true,
      };
      if (areUserRolesIn(user?.roles, [Roles.QaSpecialist])) {
        delete params.startDate;
        delete params.endDate;
        params.action_required = true;
      }
      dispatch(
        sessionActions.getList({
          params,
          getAccessTokenSilently,
          signal,
        }),
      );
    }

    if (areUserRolesIn(user?.roles, [Roles.Admin, Roles.ClientSuccessManager, Roles.Biller])) {
      dispatch(
        btSupervisionActions.getList({
          params: { startDate: supervisionNoteStartDate, endDate },
          getAccessTokenSilently,
        }),
      );
    }

    if (areUserRolesIn(user?.roles, [Roles.Admin, Roles.ClientSuccessManager, Roles.Biller])) {
      dispatch(
        parentSupervisionActions.getList({
          params: { startDate: supervisionNoteStartDate, endDate },
          getAccessTokenSilently,
        }),
      );
      dispatch(
        groupParentSupervisionActions.getList({
          params: { startDate: supervisionNoteStartDate, endDate },
          getAccessTokenSilently,
        }),
      );
    }

    if (areUserRolesIn(user?.roles, [Roles.Admin, Roles.ClientSuccessManager, Roles.Biller])) {
      dispatch(
        clientAssessmentActions.getList({
          params: { startDate: supervisionNoteStartDate, endDate },
          getAccessTokenSilently,
        }),
      );
    }

    if (
      !areUserRolesIn(user?.roles, [
        Roles.Guardian,
        Roles.Vbt,
        Roles.AssistantAssessor,
        Roles.Bcba,
        Roles.QaSpecialist,
      ])
    ) {
      dispatch(
        clientSupervisionActions.getList({
          params: {
            startDate: supervisionNoteStartDate,
            endDate,
          },
          getAccessTokenSilently,
        }),
      );
    }

    //Fetch supervisions for AA and BCBA with different params
    if (areUserRolesIn(user?.roles, [Roles.Bcba, Roles.AssistantAssessor])) {
      //Fetch only assesments for Assistant assessor
      dispatch(
        clientAssessmentActions.getList({
          params: { status: SupervisionNoteStatus.Started, action_required: true },
          getAccessTokenSilently,
        }),
      );

      if (areUserRolesIn(user?.roles, [Roles.Bcba])) {
        const params = { status_id: 1, action_required: true };
        dispatch(clientSupervisionActions.getList({ params, getAccessTokenSilently }));
        dispatch(parentSupervisionActions.getList({ params, getAccessTokenSilently }));
        dispatch(btSupervisionActions.getList({ params, getAccessTokenSilently }));
        dispatch(
          groupParentSupervisionActions.getList({
            params: { status: SupervisionNoteStatus.Started, action_required: true },
            getAccessTokenSilently,
          }),
        );
      }
    }

    if (
      areUserRolesIn(user?.roles, [
        Roles.Admin,
        Roles.Bcba,
        Roles.AssistantAssessor,
        Roles.ClientSuccessManager,
        Roles.Biller,
      ])
    ) {
      dispatch(treatmentPlanActions.getTreatmentPlanStatuses({ getAccessTokenSilently }));
      dispatch(
        treatmentPlanActions.getTreatmentPlanByQuery({
          query: {
            status: [TreatmentPlanStatus.Started, TreatmentPlanStatus.Editing].join(','),
            expand: 'treatment_recommendation',
            action_required: areUserRolesIn(user?.roles, [Roles.AssistantAssessor, Roles.Bcba])
              ? true
              : false,
          },
          getAccessTokenSilently,
          skipCurrentTpChange: true,
        }),
      );
    }

    return () => {
      dispatch(sessionActions.clearSession());
      dispatch(clientSupervisionActions.clearState());
      dispatch(parentSupervisionActions.clearState());
      dispatch(btSupervisionActions.clearState());
      dispatch(clientAssessmentActions.clearState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.roles]);

  useEffect(() => {
    if (!clientList.length) {
      if (
        areUserRolesIn(user?.roles, [
          Roles.Bcba,
          Roles.AssistantAssessor,
          Roles.Admin,
          Roles.ClientSuccessManager,
          Roles.Biller,
        ])
      ) {
        dispatch(clientActions.getList({ getAccessTokenSilently }));
      }
    }
    return () => {
      dispatch(clientActions.clearSelectedClient());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientList.length]);

  useEffect(() => {
    if (!client?.active_treatment_plan_id) return;
    dispatch(
      treatmentPlanActions.getTreatmentPlanById({
        treatmentPlanId: client.active_treatment_plan_id,
        expand: ['client_and_family_background', 'medical_history'],
        getAccessTokenSilently,
      }),
    );

    return () => {
      dispatch(treatmentPlanActions.clearTreatmentPlan());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  const [recentSessions, clinicalQAReviewedSessions, actionRequiredVBTSessions] = useMemo(() => {
    return getSessionCardData(
      sessions,
      user?.roles,
      showCQAReviewedSessionsSection,
      showActionRequiredVBTSection,
    );
  }, [sessions, user, showCQAReviewedSessionsSection]);

  const navigateToStartSession = () => {
    history.navigate('/new-session');
  };

  const loading =
    authLoading ||
    sessionLoading ||
    btSupervisionLoading ||
    parentSupervisionLoading ||
    clientSupervisionsLoading ||
    groupSupervisionsLoading ||
    treatmentPlansLoading ||
    clientAssessmentLoading;

  return (
    <PageContainer
      dataTestId="dashboardContainer"
      sx={{
        px: {
          xs: 2,
          sm: 3,
          md: 3,
          lg: 4,
        },
      }}
    >
      {areUserRolesIn(user?.roles, [Roles.Prbt, Roles.Vbt]) && (
        <Box sx={{ pt: 5, display: 'flex', alignItems: 'flex-start' }}>
          {loading ? (
            <FHSkeletonLoader width={200} height={45} />
          ) : (
            <FHButton
              data-testid="startNewSessionButton"
              sx={{
                width: 200,
                height: 45,
                borderRadius: '8px',
                boxShadow: 'none',
              }}
              onClick={navigateToStartSession}
            >
              {t('sections.startNewSession')}
            </FHButton>
          )}
        </Box>
      )}

      {/* SESSIONS - VBT action needed section */}
      {showActionRequiredVBTSection && (
        <FBoxContainer
          dataTestId="recentSessionNotes"
          titleVariant="h4"
          sx={{
            ...boxContainerStyle,
            flexGrow: 0,
          }}
          headerStyle={boxContainerHeaderStyle}
          contentStyle={contentStyle}
        >
          <Typography fontSize={21} textAlign="left" mb={3} color={FORTA_BLUE.primary}>
            {t('sections.actionNeeded')}
          </Typography>
          <RecentSessions loading={loading} sessions={actionRequiredVBTSessions} />
        </FBoxContainer>
      )}

      {/* SESSIONS - Recent open and closed session section */}
      {showRecentSessionsSection && (
        <FBoxContainer
          dataTestId="recentSessionNotes"
          titleVariant="h4"
          sx={boxContainerStyle}
          headerStyle={boxContainerHeaderStyle}
          contentStyle={contentStyle}
        >
          <Typography fontSize={21} textAlign="left" mb={3} color={FORTA_BLUE.primary}>
            {t('sections.sessionNotes')}
          </Typography>
          <RecentSessions loading={loading} sessions={recentSessions} />
        </FBoxContainer>
      )}

      {/* SESSIONS - Clinical QA reviewed section */}
      {showCQAReviewedSessionsSection && (
        <FBoxContainer
          dataTestId="recentSessionNoteReviews"
          titleVariant="h4"
          sx={boxContainerStyle}
          headerStyle={boxContainerHeaderStyle}
          contentStyle={contentStyle}
        >
          <Typography fontSize={21} textAlign="left" mb={3} color={FORTA_BLUE.primary}>
            {t('sections.sessionReviews')}
          </Typography>
          <QAReviewedSessionNotes loading={loading} sessions={clinicalQAReviewedSessions} />
        </FBoxContainer>
      )}

      {/* SESSIONS - Awaiting guardian signature section */}
      {areUserRolesIn(user?.roles, [Roles.Guardian, Roles.Vbt]) && !hideClinicalFeatures && (
        <FBoxContainer
          dataTestId="guardianSessionNotes"
          titleVariant="h4"
          sx={{
            ...boxContainerStyle,
            flexGrow: 0,
          }}
          headerStyle={boxContainerHeaderStyle}
          contentStyle={contentStyle}
        >
          <Typography fontSize={21} textAlign="left" mb={3} color={FORTA_BLUE.primary}>
            {areUserRolesIn(user?.roles, [Roles.Vbt])
              ? t('sections.pendingGuardian')
              : t('sections.signatureNeeded')}
          </Typography>
          <RecentSessions
            loading={loading}
            sessions={awaitingGuardianSignatureSession}
            noItemsMessage={t('messages.guardianCaughtUp')}
          />
        </FBoxContainer>
      )}

      {showPreClinicalFeatures && <YourActivities />}

      {areUserRolesIn(user?.roles, [Roles.Guardian]) && <RelationshipManager />}

      {/* Action required section */}
      {areUserRolesIn(user?.roles, [Roles.Bcba, Roles.AssistantAssessor]) && (
        <FBoxContainer
          dataTestId="actionRequiredTreatmentPlans"
          titleVariant="h4"
          sx={boxContainerStyle}
          headerStyle={boxContainerHeaderStyle}
          contentStyle={contentStyle}
        >
          <Typography fontSize={21} textAlign="left" mb={1} color={FORTA_BLUE.primary}>
            {t('sections.signatureRequired')}
          </Typography>
          <ActionRequired
            loading={loading}
            treatmentPlans={editingTps}
            clientSupervisions={clientSupervisionList}
            groupSupervisions={groupParentSupervisionList}
            parentSupervisions={parentSupervisionList}
            btSupervisions={btSupervisionList}
            clientAssessments={clientAssessmentList}
          />
        </FBoxContainer>
      )}

      {/* TREATMENT PLAN - In-progress treatment plan section */}
      {areUserRolesIn(user?.roles, [Roles.Admin, Roles.ClientSuccessManager, Roles.Biller]) && (
        <FBoxContainer
          dataTestId="inProgressTreatmentPlans"
          titleVariant="h4"
          sx={boxContainerStyle}
          headerStyle={boxContainerHeaderStyle}
          contentStyle={contentStyle}
        >
          <Typography fontSize={21} textAlign="left" mb={3} color={FORTA_BLUE.primary}>
            {t('sections.treatmentPlans')}
          </Typography>
          <InProgressTreatmentPlans loading={loading} treatmentPlans={editingTps} />
        </FBoxContainer>
      )}

      {areUserRolesIn(user?.roles, [Roles.QaSpecialist]) && (
        <FBoxContainer
          dataTestId="inProgressTreatmentPlans"
          titleVariant="h4"
          sx={boxContainerStyle}
          headerStyle={boxContainerHeaderStyle}
          contentStyle={contentStyle}
        >
          <Typography fontSize={21} textAlign="left" mb={3} color={FORTA_BLUE.primary}>
            {t('sections.clinicalReviews')}
          </Typography>
          <InProgressCQAReviews loading={loading} sessions={sessions} />
        </FBoxContainer>
      )}
      {/* SUPERVISIONS - All recent supervision section */}
      {!areUserRolesIn(user?.roles, [
        Roles.Guardian,
        Roles.Vbt,
        Roles.Bcba,
        Roles.AssistantAssessor,
        Roles.QaSpecialist,
      ]) && (
        <FBoxContainer
          dataTestId="recentSupervisions"
          titleVariant="h4"
          sx={boxContainerStyle}
          headerStyle={boxContainerHeaderStyle}
        >
          <Typography fontSize={21} textAlign="left" mb={3} color={FORTA_BLUE.primary}>
            {areUserRolesIn(user?.roles, [Roles.Prbt])
              ? t('sections.clientSupervisionNotes')
              : t('sections.documents')}
          </Typography>
          <RecentSupervisions
            loading={loading}
            clientSupervisions={clientSupervisionList}
            btSupervisions={btSupervisionList}
            parentSupervisions={parentSupervisionList}
            groupSupervisions={groupParentSupervisionList}
          />
        </FBoxContainer>
      )}
      {/* CLIENT INSIGHT */}
      {areUserRolesIn(user?.roles, [
        Roles.AssistantAssessor,
        Roles.Admin,
        Roles.ClientSuccessManager,
        Roles.Biller,
      ]) && <ClientInsights />}

      {/* WEEKLY PROGRESS */}
      {areUserRolesIn(user?.roles, [Roles.Prbt, Roles.Guardian]) && !hideClinicalFeatures && (
        <FBoxContainer
          dataTestId="utilizationSummary"
          titleVariant="h4"
          sx={boxContainerStyle}
          headerStyle={boxContainerHeaderStyle}
        >
          <Box>
            <SessionUtilizationSummary />
          </Box>
        </FBoxContainer>
      )}
    </PageContainer>
  );
}

export default GeneralDashboard;
